/**
 * useModalManagement Hook
 *
 * Centralized modal state management for all application modals.
 * Extracted from AppContent.js as part of Phase 1 refactoring.
 *
 * Features:
 * - Manages all modal open/close states
 * - Provides helper functions for each modal
 * - Integrates with UIStore for persistent modals
 * - Manages transient modal states (local)
 *
 * @returns {Object} Modal states and control functions
 */

import { useState } from "react";
import { useUIStore } from "../stores";
import logger from "../utils/logger";

export const useModalManagement = () => {
  // Modal states from UIStore (some persist across sessions)
  const {
    deleteCredentialModal,
    openDeleteCredentialModal,
    closeDeleteCredentialModal,
    setDeleteCredentialModalDeleting,
    isBulkStatusModalOpen,
    setBulkStatusModalOpen,
    showPasswordModal,
    setPasswordModalOpen,
    isAdvancedSettingsOpen,
    setAdvancedSettingsOpen,
    isInstallationManagementOpen,
    managedOrganizationId,
    setInstallationManagementOpen,
  } = useUIStore();

  // Transient modal states (local to component, don't persist)
  const [isDeviceClaimingModalOpen, setIsDeviceClaimingModalOpen] =
    useState(false);
  const [isInstallationActionsOpen, setIsInstallationActionsOpen] =
    useState(false);
  const [isOrganizationManagementOpen, setIsOrganizationManagementOpen] =
    useState(false);

  // Helper functions for common modal operations
  const openDeviceClaimingModal = () => {
    setIsDeviceClaimingModalOpen(true);
    logger.log("[useModalManagement] Device claiming modal opened");
  };

  const closeDeviceClaimingModal = () => {
    setIsDeviceClaimingModalOpen(false);
    logger.log("[useModalManagement] Device claiming modal closed");
  };

  const openInstallationActionsModal = () => {
    setIsInstallationActionsOpen(true);
    logger.log("[useModalManagement] Installation actions modal opened");
  };

  const closeInstallationActionsModal = () => {
    setIsInstallationActionsOpen(false);
    logger.log("[useModalManagement] Installation actions modal closed");
  };

  const openOrganizationManagementModal = () => {
    setIsOrganizationManagementOpen(true);
    logger.log("[useModalManagement] Organization management modal opened");
  };

  const closeOrganizationManagementModal = () => {
    setIsOrganizationManagementOpen(false);
    logger.log("[useModalManagement] Organization management modal closed");
  };

  const openBulkStatusModal = () => {
    setBulkStatusModalOpen(true);
    logger.log("[useModalManagement] Bulk status modal opened");
  };

  const closeBulkStatusModal = () => {
    setBulkStatusModalOpen(false);
    logger.log("[useModalManagement] Bulk status modal closed");
  };

  const openPasswordModal = () => {
    setPasswordModalOpen(true);
    logger.log("[useModalManagement] Password modal opened");
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    logger.log("[useModalManagement] Password modal closed");
  };

  const openAdvancedSettings = () => {
    setAdvancedSettingsOpen(true);
    logger.log("[useModalManagement] Advanced settings opened");
  };

  const closeAdvancedSettings = () => {
    setAdvancedSettingsOpen(false);
    logger.log("[useModalManagement] Advanced settings closed");
  };

  return {
    // Delete Credential Modal (from UIStore)
    deleteCredentialModal,
    openDeleteCredentialModal,
    closeDeleteCredentialModal,
    setDeleteCredentialModalDeleting,

    // Bulk Status Modal (from UIStore)
    isBulkStatusModalOpen,
    setBulkStatusModalOpen,
    openBulkStatusModal,
    closeBulkStatusModal,

    // Password Modal (from UIStore)
    showPasswordModal,
    openPasswordModal,
    closePasswordModal,

    // Advanced Settings (from UIStore)
    isAdvancedSettingsOpen,
    openAdvancedSettings,
    closeAdvancedSettings,

    // Installation Management Modal (from UIStore)
    isInstallationManagementOpen,
    managedOrganizationId,
    setInstallationManagementOpen,

    // Device Claiming Modal (local state)
    isDeviceClaimingModalOpen,
    openDeviceClaimingModal,
    closeDeviceClaimingModal,
    setIsDeviceClaimingModalOpen,

    // Installation Actions Modal (local state)
    isInstallationActionsOpen,
    openInstallationActionsModal,
    closeInstallationActionsModal,
    setIsInstallationActionsOpen,

    // Organization Management Modal (local state)
    isOrganizationManagementOpen,
    openOrganizationManagementModal,
    closeOrganizationManagementModal,
    setIsOrganizationManagementOpen,
  };
};
